import Swal from 'sweetalert2'
import {TCreditCardRefund} from '../../../coreGlobal/models/billing'
import {retryCieloRefund, manualSettleCieloRefund} from './core/_requests'
import useAuthorization from '../../../hooks/useAuthorization'

interface CreditCardRefundStatusProps {
  refund: TCreditCardRefund
  onUpdated: () => void
}

const statusBadge: Record<TCreditCardRefund['status'], {label: string; color: string}> = {
  pending: {label: 'Estorno pendente', color: 'badge badge-warning'},
  processing: {label: 'Estornando...', color: 'badge badge-warning'},
  success: {label: 'Estornado', color: 'badge badge-success'},
  failed: {label: 'Falha no estorno', color: 'badge badge-danger'},
  manual: {label: 'Baixa manual', color: 'badge badge-secondary'},
}

export const CreditCardRefundStatus = ({refund, onUpdated}: CreditCardRefundStatusProps) => {
  const isAuthorizedToRetry = useAuthorization('distracts.retry_refund')
  const isAuthorizedToManualSettle = useAuthorization('distracts.manual_settle_refund')

  const canAct = refund.status === 'pending' || refund.status === 'failed'
  const badge = statusBadge[refund.status]

  const handleRetry = () => {
    Swal.fire({
      title: 'Tentar o estorno novamente via Cielo?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary'},
    }).then(async (result) => {
      if (!result.isConfirmed) return
      try {
        await retryCieloRefund(refund.id)
        await Swal.fire({icon: 'success', title: 'Estorno reenviado!'})
        onUpdated()
      } catch (error: any) {
        await Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error?.response?.data?.message ?? 'Erro ao reenviar o estorno.',
        })
      }
    })
  }

  const handleManualSettle = () => {
    Swal.fire({
      title: 'Confirmar baixa manual do estorno?',
      text: 'Isso indica que o estorno foi resolvido fora do sistema, sem nova cobrança à Cielo.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      customClass: {confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary'},
    }).then(async (result) => {
      if (!result.isConfirmed) return
      try {
        await manualSettleCieloRefund(refund.id)
        await Swal.fire({icon: 'success', title: 'Baixa manual registrada!'})
        onUpdated()
      } catch (error: any) {
        await Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error?.response?.data?.message ?? 'Erro ao registrar a baixa manual.',
        })
      }
    })
  }

  return (
    <div className='d-flex flex-column gap-1'>
      <div className='d-flex align-items-center flex-wrap gap-2'>
        <a className={badge.color}>{badge.label}</a>
        {canAct && isAuthorizedToRetry && (
          <button type='button' className='btn btn-sm btn-light-danger' onClick={handleRetry}>
            Tentar novamente
          </button>
        )}
        {canAct && isAuthorizedToManualSettle && (
          <button
            type='button'
            className='btn btn-sm btn-light-secondary'
            onClick={handleManualSettle}
          >
            Baixa manual
          </button>
        )}
      </div>
      {(refund.cieloTid || refund.cieloReturnCode || refund.cieloReturnMessage) && (
        <div className='text-muted fs-8'>
          {refund.cieloTid && (
            <div>
              <b>Tid:</b> {refund.cieloTid}
            </div>
          )}
          {refund.cieloReturnCode && (
            <div>
              <b>Código de retorno:</b> {refund.cieloReturnCode}
            </div>
          )}
          {refund.cieloReturnMessage && (
            <div>
              <b>Mensagem:</b> {refund.cieloReturnMessage}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
